import { z } from 'zod';

import {
  GithubIssueRelationshipsSchema,
  IssuePrioritySchema,
  IssueStateSchema,
} from './issues';
import { PullRequestSummarySchema } from './pull-requests';
import { WorkflowStatusSchema } from './workflow-status';

// ---------------------------------------------------------------------------
// Persisted work items — the viewer-INDEPENDENT facts the Convex mirror stores,
// one row per source object (a GitHub PR, a GitHub issue, a Linear issue). These
// supersets carry the raw workflow primitives the thin Summary/BoardItem
// contracts drop (`work-item-status.ts` derives `workflowStatus` then discards
// `reviewDecision`/`stateReason`/`stateType`/lifecycle stamps), so `deriveWorkflow`
// can be recomputed from a stored row without re-fetching from GitHub/Linear.
//
// The board's relationship buckets (authored/assigned/…) are a per-viewer
// projection and are deliberately NOT stored here. These schemas are the single
// source of truth shared by the consolidation mappers (which build them), the
// Convex table validators in `convex/schema.ts` (kept in sync field-for-field),
// and the persistence tests. Dates are ISO strings, as everywhere in contracts.
// ---------------------------------------------------------------------------

/**
 * A GitHub pull request, as stored. The `PullRequestSummary` fields (incl.
 * `headSha`/`baseSha`) plus GitHub's raw review/lifecycle primitives.
 */
export const PersistedPullRequestSchema = PullRequestSummarySchema.omit({
  // Delivery claims are a live, bounded planner signal; the mirror keeps `body`.
  delivers: true,
}).extend({
  /** Stable upsert key: `owner/repo#123` (the board id form). */
  sourceId: z.string().min(1),
  /** GitHub's aggregate review decision (APPROVED/CHANGES_REQUESTED/…); `null` when none. */
  reviewDecision: z.string().nullable(),
  /** ISO close time; `null` while open. */
  closedAt: z.iso.datetime().nullable(),
  /** ISO merge time; `null` unless merged. */
  mergedAt: z.iso.datetime().nullable(),
  /** PR prose used by workflow/link classification. */
  body: z.string().optional(),
  /** Bounded review events, including the timestamps workflow analysis needs. */
  reviewEvents: z
    .array(
      z.object({
        state: z.string(),
        submittedAt: z.iso.datetime().nullable(),
        author: z.string(),
      }),
    )
    .optional(),
  /** True when GitHub reported more review events than the mirror retained. */
  reviewsTruncated: z.boolean().optional(),
  /** True when GitHub reported more changed paths than the mirror retained. */
  filesTruncated: z.boolean().optional(),
});
export type PersistedPullRequest = z.infer<typeof PersistedPullRequestSchema>;

/**
 * A GitHub issue, as stored. `repo`/`number` are explicit (not parsed from
 * `sourceId`) so the `by_org_repo` index and the future scan seam can key on them.
 */
export const PersistedGithubIssueSchema = z.object({
  /** Stable upsert key: `github:owner/repo#123`. */
  sourceId: z.string().min(1),
  /** `owner/repo`. */
  repo: z.string().min(1),
  number: z.number().int().positive(),
  /** Human reference: `#123`. */
  displayKey: z.string().min(1),
  /** Grouping scope: `owner/repo`. */
  scope: z.string().min(1),
  title: z.string(),
  url: z.url(),
  state: IssueStateSchema,
  author: z.string(),
  assignees: z.array(z.string()),
  comments: z.number().int().nonnegative(),
  labels: z.array(z.string()),
  relationships: GithubIssueRelationshipsSchema,
  /** GitHub IssueStateReason (`COMPLETED`/`NOT_PLANNED`/…); `null` when open/unset. */
  stateReason: z.string().nullable(),
  /** ISO close time; `null` while open. */
  closedAt: z.iso.datetime().nullable(),
  workflowStatus: WorkflowStatusSchema,
  workflowStatusSince: z.iso.datetime(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type PersistedGithubIssue = z.infer<typeof PersistedGithubIssueSchema>;

/**
 * A Linear issue, as stored. `workspaceId` is the Clerk connection id embedded in
 * `sourceId`; the raw `stateType`/stage stamps drive `deriveWorkflow` recompute.
 */
export const PersistedLinearIssueSchema = z.object({
  /** Stable upsert key: `linear:<workspaceId>:ENG-45`. */
  sourceId: z.string().min(1),
  /** The Linear workspace connection id (the `<workspaceId>` in `sourceId`). */
  workspaceId: z.string().min(1),
  /** Human reference: `ENG-45`. */
  displayKey: z.string().min(1),
  /** Grouping scope: `Workspace/TEAM`. */
  scope: z.string().min(1),
  title: z.string(),
  url: z.url(),
  state: IssueStateSchema,
  author: z.string(),
  assignees: z.array(z.string()),
  /** Linear priority (0–4); `null` when unset. */
  priority: IssuePrioritySchema.nullable(),
  comments: z.number().int().nonnegative(),
  /** True when `comments` is a capped lower bound (Linear exposes no exact count). */
  commentsTruncated: z.boolean(),
  /** Linear workflow-state `type` (backlog/started/completed/…); `null` when absent. */
  stateType: z.string().nullable(),
  /** ISO stage-entry stamps; `null` when the state hasn't been reached. */
  startedAt: z.iso.datetime().nullable(),
  completedAt: z.iso.datetime().nullable(),
  canceledAt: z.iso.datetime().nullable(),
  workflowStatus: WorkflowStatusSchema,
  workflowStatusSince: z.iso.datetime(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});
export type PersistedLinearIssue = z.infer<typeof PersistedLinearIssueSchema>;

/**
 * The viewer-independent rows a board fetch produces, split by source table — the
 * payload the dashboard route mirrors into Convex. Each list is already deduped.
 */
export interface WorkItemsPersistPayload {
  readonly pullRequests: readonly PersistedPullRequest[];
  readonly githubIssues: readonly PersistedGithubIssue[];
  readonly linearIssues: readonly PersistedLinearIssue[];
}
